import React, { useState, useEffect, useRef } from 'react';
import '../pages/Sidebar.css';

/**
 * UnifiedSidebar - A single sidebar for both Social and LMS contexts.
 * @param {Object} props
 * @param {Array} props.menuSections - Array of nav sections: [{ section: string, items: [{ id, label, icon, onClick, badge }] }]
 * @param {boolean} props.showRoleSwitcher - Whether to show the role switcher dropdown
 * @param {Array} props.roleOptions - Role dropdown options [{ value, label }]
 * @param {string} props.activeRole - Currently selected role value
 * @param {function} props.onRoleChange - Handler for role switch
 * @param {function} props.onSectionChange - Handler for nav section/button change (for Social)
 * @param {function} props.onLogout - Handler for logout
 * @param {string} props.logoText - Optional logo string
 * @param {string} props.logoSrc - Optional logo image source
 * @param {string} props.logoHref - Optional logo link
 * @param {boolean} props.showHome - Whether to show the Home button at the top
 * @param {function} props.onHomeClick - Handler for Home button
 * @param {string} props.activeSection - Active nav section/button id
 * @param {Object} props.badges - Optional map of badge counts { [badgeType]: number }
 */
const UnifiedSidebar = ({
  menuSections = [],
  showRoleSwitcher = false,
  roleOptions = [],
  activeRole = '',
  onRoleChange = () => {},
  onSectionChange = () => {},
  onLogout = () => {},
  logoText = 'VirtuLingu',
  logoSrc = '/logo.png',
  logoHref = '/app/home',
  showHome = true,
  onHomeClick = () => {},
  activeSection = '',
  badges = {},
  dense = false,
  onCollapseChange = () => {},
  ...props
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const navRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  // Calculate total navigation items to determine if scrolling is needed
  const totalNavItems = menuSections.reduce((total, section) => total + section.items.length, 0);
  
  // Effective collapsed state: collapsed unless pinned open OR hover-expanded
  const effectiveCollapsed = collapsed && !isHoverExpanded;
  const needsScrollableNav = totalNavItems > 8 && !effectiveCollapsed;

  const checkScrollability = () => {
    if (navRef.current && needsScrollableNav) {
      const { scrollTop, scrollHeight, clientHeight } = navRef.current;
      setCanScrollUp(scrollTop > 0);
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1);
    } else {
      setCanScrollUp(false);
      setCanScrollDown(false);
    }
  };

  const scroll = (direction) => {
    if (navRef.current && !isScrolling && needsScrollableNav) {
      setIsScrolling(true);
      const scrollAmount = direction === 'up' ? -80 : 80;
      navRef.current.scrollBy({ top: scrollAmount, behavior: 'smooth' });
      setTimeout(() => setIsScrolling(false), 300);
    }
  };

  // Removed auto-collapse on resize - sidebar is now user-controlled

  useEffect(() => {
    checkScrollability();
    const nav = navRef.current;
    if (nav && needsScrollableNav) {
      nav.addEventListener('scroll', checkScrollability);
      return () => nav.removeEventListener('scroll', checkScrollability);
    }
  }, [menuSections, collapsed, needsScrollableNav]);

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
    setIsHoverExpanded(false);
    // Notify parent component of collapse state change
    if (props.onCollapseChange) {
      props.onCollapseChange(!collapsed);
    }
  };

  const handleMouseEnter = () => {
    if (collapsed) {
      setIsHoverExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHoverExpanded(false);
  };

  const handleNavClick = (itemOnClick) => {
    if (itemOnClick) itemOnClick();
    // Auto-collapse after navigation
    setCollapsed(true);
    setIsHoverExpanded(false);
  };

  return (
    <div 
      className={`sidebar${effectiveCollapsed ? ' collapsed' : ''}${dense ? ' dense' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    > 
      <div className="sidebar-header">
        <button className="collapse-button" onClick={toggleSidebar} aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {collapsed ? (
              <polyline points="9 6 15 12 9 18"></polyline>
            ) : (
              <polyline points="15 18 9 12 15 6"></polyline>
            )}
          </svg>
        </button>
      </div>
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        {/* Scroll Up Button */}
        {canScrollUp && (
          <button
            onClick={() => scroll('up')}
            className="sidebar-scroll-btn up"
          >
            ↑
          </button>
        )}
        <nav 
          className="sidebar-nav"
          ref={navRef}
          style={{
            maxHeight: needsScrollableNav ? '75vh' : 'none',
            overflowY: needsScrollableNav ? 'auto' : 'visible',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            // keep nav content tight; the scroll buttons are positioned absolute, so avoid adding extra padding gaps
            paddingTop: canScrollUp ? (dense ? '6px' : '12px') : '0',
            paddingBottom: canScrollDown ? (dense ? '6px' : '12px') : '0'
          }}
        >
          <style>
            {`
              .sidebar-nav::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
        <ul>
          {showHome && (
            <li>
              <button 
                className={`nav-item${activeSection === 'home' ? ' active' : ''}`}
                onClick={() => handleNavClick(onHomeClick)}
              >
                <span className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </span>
                {!effectiveCollapsed && <span className="label">Home</span>}
              </button>
            </li>
          )}
          {showRoleSwitcher && !effectiveCollapsed && (
            <li style={{ margin: '10px 0' }}>
              <label htmlFor="sidebar-role-switcher" className="role-switcher-label">Role</label>
              <select
                id="sidebar-role-switcher"
                value={activeRole}
                onChange={e => onRoleChange(e.target.value)}
                className="role-switcher-dropdown"
              >
                {roleOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </li>
          )}
          {menuSections.map(section => (
            <React.Fragment key={section.section}>
              {!effectiveCollapsed && section.section && <li className="sidebar-section-title">{section.section}</li>}
              {section.items.map(item => (
                <li key={item.id}>
                  <button
                    className={`nav-item${activeSection === item.id ? ' active' : ''}`}
                    onClick={() => handleNavClick(item.onClick)}
                  >
                    <span className="icon">{item.icon}</span>
                    {!effectiveCollapsed && <span className="label">{item.label}</span>}
                    {!effectiveCollapsed && item.badge && badges[item.badge] > 0 && (
                      <span className="badge">{badges[item.badge]}</span>
                    )}
                  </button>
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>
        </nav>
        {/* Scroll Down Button */}
        {canScrollDown && (
          <button
            onClick={() => scroll('down')}
            className="sidebar-scroll-btn down"
          >
            ↓
          </button>
        )}
      </div>
      <div className="sidebar-footer">
        <button className="logout-nav-item" onClick={() => handleNavClick(onLogout)}>
          <span className="icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1-2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </span>
          {!effectiveCollapsed && <span className="label">Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default UnifiedSidebar;
